import { call } from "redux-saga/effects";
import action from "../../decorators/action.decorator";
import reducer, { InitSchemaReducer } from "../../decorators/reducer.decorator";
import { DIContainerKeys } from "../../di-container/di-container.keys";
import { Entities } from "../../store/entities/entities.enum";
import { HttpService } from "../http.service";
import { ReducerMethods } from "../../store/reducer.methods";
import { API_DOMENS, BASE_API_PATHS } from "../../../constants/apis.constants";
import type GetCompanyTechSpendDto from "./dto/get-company-techspend.dto";
import loader from "../../decorators/loader.decorator";
import { Loaders } from "../../store/loaders/loaders.enum";
import type UpdateCategoryVisibilityDto from "./dto/update-categoru-visibility.dto";
import type ResetCategoryDto from "./dto/reset-categories.dto";
import flagger from "../../decorators/flagger.decorator";
import { Flaggers } from "../../store/flaggers/flaggers.enum";
import type UpdateCategoryDto from "./dto/update-category.dto";
import { CallToast } from "../../../Components/PriceCalculator/modals/CustomToast";

export const CompanyTechSpendEffectActions = {
  getTechnologySpend: `${Entities.CompanyTechSpend}_getTechnologySpend`,
  updateCategoryVisibility: `${Entities.CompanyTechSpend}_updateCategoryVisibility`,
  resetCategories: `${Entities.CompanyTechSpend}_resetCategories`,
  updateCategory: `${Entities.CompanyTechSpend}_updateCategory`,
};

const initSchema: InitSchemaReducer = {
  definitions: {  },
  options: { idAttribute: '' },
};

@reducer({
  entityName: Entities.CompanyTechSpend,
  initSchema,
  aliasName: DIContainerKeys.CompanyTechSpendService,
})
export class CompanyTechSpendService extends HttpService {
  @loader(Loaders.GetTechnologySpendLoader)
  @loader(Loaders.GetTechnologySpendForDashboardLoader)
  @action(DIContainerKeys.CompanyTechSpendService)
  public *getTechnologySpend(dto: GetCompanyTechSpendDto): Generator<any, void, any> {
    const loader = dto.loader ?? Loaders.GetTechnologySpendForDashboardLoader;
    yield this.setLoaderState(loader, true);
    const formData = this.toFormData({
      ...(!dto.isSaas && dto.customerId ? {
        ObjId_Customer: dto.customerId,
        optionEnum98: dto.customerId,
        optionEnum99: 24,
      } : {
        ObjId_Customer: "0",
        optionEnum99: 32,
      }),
      FromDate: dto.fromDate,
      ToDate: dto.toDate,
    });
    const response: any = yield call(
      this.post,
      {
        url: BASE_API_PATHS.getTechnologySpend,
        domain: API_DOMENS.TechSpend,
        body: formData,
      },
      ReducerMethods.UPDATE
    );
    if (!response?.mr?.Success) {
      yield this.setLoaderState(loader, false);
      return;
    }
    yield this.setEntityStateById(Entities.CompanyTechSpend, dto.customerId, response);
    yield this.setLoaderState(loader, false);
  }

  @action(DIContainerKeys.CompanyTechSpendService)
  public *updateCategoryVisibility(dto: UpdateCategoryVisibilityDto): Generator<any, void, any> {
    yield call(
      this.post,
      {
        url: BASE_API_PATHS.updateCategorySortVisibility,
        domain: API_DOMENS.PSA_API,
        body: this.toFormData({
          CategorySortVisibility: dto.categorySortVisibility,
          isSubcat: dto.isSubcat,
          optionEnum98: dto.clientId,
        }),
      },
      ReducerMethods.UPDATE
    );
  }

  @flagger(Flaggers.ResetCategoryFlagger)
  @loader(Loaders.ResetCategoryLoader)
  @action(DIContainerKeys.CompanyTechSpendService)
  public *resetCategories(dto: ResetCategoryDto): Generator<any, void, any> {
    yield this.setLoaderState(Loaders.ResetCategoryLoader, true);
    const response: any = yield call(
      this.post,
      {
        url: BASE_API_PATHS.resetCategoryToDefault,
        domain: API_DOMENS.PSA_API,
        body: this.toFormData({
          optionEnum98: dto.clientId,
          resetType: dto.resetType,
          resetForSo: dto.resetForSO,
        }),
      },
      ReducerMethods.UPDATE
    );
    yield this.setLoaderState(Loaders.ResetCategoryLoader, false);
    yield this.setFlaggerState(Flaggers.ResetCategoryFlagger, null);
    if (response?.Success) {
      if (dto.clientId) {
        yield this.getTechnologySpend({
          customerId: dto.clientId?.toString(),
          fromDate: dto.fromDate,
          toDate: dto.toDate,
          loader: Loaders.GetTechnologySpendLoader,
        });
      }
      if (dto?.callback) {
        dto?.callback();
      }
    }
  }

  @loader(Loaders.UpdateTechSpendCategoryLoader)
  @flagger(Flaggers.UpdateTechSpendCategoryFlagger)
  @action(DIContainerKeys.CompanyTechSpendService)
  public *updateCategory(dto: UpdateCategoryDto): any {
    yield this.setLoaderState(Loaders.UpdateTechSpendCategoryLoader, true);
    yield this.setFlaggerState(Flaggers.UpdateTechSpendCategoryFlagger, { isLoading: true });
    const body = dto.category;
    const response = yield call(
      this.post, 
      {
        url: BASE_API_PATHS.createProductCategory,
        domain: API_DOMENS.PSA_API,
        body: this.toFormData({
          ...body,
          optionEnum98: dto.clientId,
        }),
      },
      ReducerMethods.UPDATE
    );
    yield this.setLoaderState(Loaders.UpdateTechSpendCategoryLoader, false);
    if (!response?.Success) {
      CallToast("Something went wrong", false, false, "v2style");
      return;
    }
    yield this.setFlaggerState(Flaggers.UpdateTechSpendCategoryFlagger, { isLoading: false, success: true });
  }
}